"use client"

import { useState, useMemo } from "react"
import { Plus, Trash2, RotateCcw, Lightbulb } from "lucide-react"
import type { Idioma } from "@/lib/landing/idioma"
import { eurosEn, numeroEn } from "./formato"

interface Plato {
  id: number
  nombre: string
  coste: number
  foodCostObjetivo: number
}

const T = {
  es: {
    platos: [
      { id: 1, nombre: "Ensalada César", coste: 2.50, foodCostObjetivo: 28 },
      { id: 2, nombre: "Pasta Carbonara", coste: 3.20, foodCostObjetivo: 25 },
      { id: 3, nombre: "Entrecot 300g", coste: 8.50, foodCostObjetivo: 32 },
      { id: 4, nombre: "Tarta de queso", coste: 1.80, foodCostObjetivo: 22 },
    ] as Plato[],
    tips: [
      { range: [0, 10], tip: "Termina en .90 o .95 (ej: 8.90€)" },
      { range: [10, 20], tip: "Termina en .50 o .90 (ej: 14.50€)" },
      { range: [20, 50], tip: "Redondea o usa .00/.50 (ej: 25€)" },
      { range: [50, 1000], tip: "Precio entero sin céntimos (ej: 45€)" },
    ],
    titulo: "Platos de la carta",
    limpiar: "Limpiar",
    tipoIva: "Tipo de IVA",
    ivaReducido: "10% (restauración)",
    ivaGeneral: "21% (general)",
    ayudaIva: null as string | null,
    phNombre: "Nombre del plato",
    coste: "Coste (€)",
    foodCostObjetivo: "Food cost objetivo (%)",
    anadir: "Añadir plato",
    preciosCalculados: "Precios calculados",
    foodCostMedio: "Food cost medio",
    margenMedio: "Margen medio",
    sinNombre: "Sin nombre",
    ivaInc: "IVA inc.",
    sinIva: "Sin IVA",
    iva: (pct: number) => `IVA (${pct}%)`,
    margen: "Margen",
    precioSugerido: "Precio sugerido",
    consejos: [
      "Precios que terminan en .90/.95 parecen más baratos",
      "Evita el símbolo € en cartas (reduce fricción)",
      "No alinear precios a la derecha (el ojo busca el más barato)",
      "Destaca platos rentables con descripciones atractivas",
    ],
  },
  en: {
    platos: [
      { id: 1, nombre: "Caesar salad", coste: 2.50, foodCostObjetivo: 28 },
      { id: 2, nombre: "Spaghetti carbonara", coste: 3.20, foodCostObjetivo: 25 },
      { id: 3, nombre: "300g entrecôte", coste: 8.50, foodCostObjetivo: 32 },
      { id: 4, nombre: "Cheesecake", coste: 1.80, foodCostObjetivo: 22 },
    ] as Plato[],
    tips: [
      { range: [0, 10], tip: "End in .90 or .95 (e.g. €8.90)" },
      { range: [10, 20], tip: "End in .50 or .90 (e.g. €14.50)" },
      { range: [20, 50], tip: "Round it or use .00/.50 (e.g. €25)" },
      { range: [50, 1000], tip: "A whole number, no cents (e.g. €45)" },
    ],
    titulo: "Menu dishes",
    limpiar: "Reset",
    tipoIva: "VAT rate",
    ivaReducido: "10% (restaurants)",
    ivaGeneral: "21% (standard)",
    ayudaIva: "In Spain, VAT (IVA) on food and drink served in restaurants and bars is 10%; the standard 21% rate applies to most other goods and services." as string | null,
    phNombre: "Dish name",
    coste: "Cost (€)",
    foodCostObjetivo: "Target food cost (%)",
    anadir: "Add dish",
    preciosCalculados: "Calculated prices",
    foodCostMedio: "Average food cost",
    margenMedio: "Average margin",
    sinNombre: "Unnamed",
    ivaInc: "incl. VAT",
    sinIva: "Excl. VAT",
    iva: (pct: number) => `VAT (${pct}%)`,
    margen: "Margin",
    precioSugerido: "Suggested price",
    consejos: [
      "Prices ending in .90/.95 feel cheaper",
      "Leave the € sign off the menu (it reduces friction)",
      "Don't right-align prices (the eye goes looking for the cheapest)",
      "Highlight your most profitable dishes with appealing descriptions",
    ],
  },
}

let nextId = 100

export default function PricingCartaCalculator({ idioma = "es" }: { idioma?: Idioma }) {
  const t = T[idioma]
  const defaultPlatos = t.platos
  const psychologicalTips = t.tips
  const eur = (n: number) => (idioma === "en" ? eurosEn(n) : `${n.toFixed(2)}€`)
  const pct1 = (n: number) => (idioma === "en" ? numeroEn(n, 1) : n.toFixed(1))
  const [platos, setPlatos] = useState<Plato[]>(defaultPlatos)
  const [ivaPct, setIvaPct] = useState(10)

  const results = useMemo(() => {
    return platos.map((p) => {
      const pvpSinIva = p.foodCostObjetivo > 0 ? p.coste / (p.foodCostObjetivo / 100) : 0
      const iva = pvpSinIva * (ivaPct / 100)
      const pvpConIva = pvpSinIva + iva
      const margen = pvpSinIva - p.coste
      const margenPct = pvpSinIva > 0 ? (margen / pvpSinIva) * 100 : 0

      // Psychological pricing suggestion
      const tip = psychologicalTips.find(
        (pt) => pvpConIva >= pt.range[0] && pvpConIva < pt.range[1]
      )?.tip

      // Suggested rounded price
      let pvpSugerido = pvpConIva
      if (pvpConIva < 10) {
        pvpSugerido = Math.ceil(pvpConIva * 10 - 0.1) / 10 + 0.4
        if (pvpSugerido > pvpConIva + 0.5) pvpSugerido = Math.ceil(pvpConIva) - 0.1
      } else if (pvpConIva < 20) {
        pvpSugerido = Math.round(pvpConIva * 2) / 2
        if (pvpSugerido < pvpConIva) pvpSugerido += 0.5
      } else {
        pvpSugerido = Math.ceil(pvpConIva)
      }

      return {
        ...p,
        pvpSinIva,
        iva,
        pvpConIva,
        margen,
        margenPct,
        tip,
        pvpSugerido,
      }
    })
  }, [platos, ivaPct, psychologicalTips])

  const totals = useMemo(() => {
    const avgFoodCost =
      platos.length > 0
        ? platos.reduce((sum, p) => sum + p.foodCostObjetivo, 0) / platos.length
        : 0
    const avgMargen =
      results.length > 0
        ? results.reduce((sum, r) => sum + r.margenPct, 0) / results.length
        : 0
    return { avgFoodCost, avgMargen }
  }, [platos, results])

  const addPlato = () => {
    setPlatos([
      ...platos,
      { id: nextId++, nombre: "", coste: 0, foodCostObjetivo: 30 },
    ])
  }

  const removePlato = (id: number) => {
    if (platos.length > 1) {
      setPlatos(platos.filter((p) => p.id !== id))
    }
  }

  const updatePlato = (id: number, field: keyof Plato, value: string | number) => {
    setPlatos(platos.map((p) => (p.id === id ? { ...p, [field]: value } : p)))
  }

  const reset = () => {
    setPlatos(defaultPlatos)
    setIvaPct(10)
  }

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
      <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-zinc-200">
        {/* Inputs */}
        <div className="p-6 md:p-8 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-zinc-900">{t.titulo}</h2>
            <button
              onClick={reset}
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              {t.limpiar}
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.tipoIva}
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setIvaPct(10)}
                className={`flex-1 py-2.5 px-4 rounded-xl font-medium transition-all ${
                  ivaPct === 10
                    ? "bg-lime-500 text-white"
                    : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                }`}
              >
                {t.ivaReducido}
              </button>
              <button
                onClick={() => setIvaPct(21)}
                className={`flex-1 py-2.5 px-4 rounded-xl font-medium transition-all ${
                  ivaPct === 21
                    ? "bg-lime-500 text-white"
                    : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                }`}
              >
                {t.ivaGeneral}
              </button>
            </div>
            {t.ayudaIva && <p className="text-xs text-zinc-500 mt-1.5">{t.ayudaIva}</p>}
          </div>

          <div className="space-y-3">
            {platos.map((p) => (
              <div key={p.id} className="bg-zinc-50 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={p.nombre}
                    onChange={(e) => updatePlato(p.id, "nombre", e.target.value)}
                    placeholder={t.phNombre}
                    className="flex-1 px-3 py-2 rounded-xl border border-zinc-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-lime-500"
                  />
                  <button
                    onClick={() => removePlato(p.id)}
                    className="p-2 text-zinc-400 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">{t.coste}</label>
                    <input
                      type="number"
                      value={p.coste || ""}
                      onChange={(e) =>
                        updatePlato(p.id, "coste", parseFloat(e.target.value) || 0)
                      }
                      placeholder="3.50"
                      min="0"
                      step="0.1"
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-lime-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">{t.foodCostObjetivo}</label>
                    <input
                      type="number"
                      value={p.foodCostObjetivo || ""}
                      onChange={(e) =>
                        updatePlato(p.id, "foodCostObjetivo", parseFloat(e.target.value) || 0)
                      }
                      placeholder="30"
                      min="1"
                      max="100"
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-lime-500"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={addPlato}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-lime-600 hover:text-lime-700"
          >
            <Plus className="w-4 h-4" />
            {t.anadir}
          </button>
        </div>

        {/* Results */}
        <div className="p-6 md:p-8 bg-zinc-50 space-y-6">
          <h2 className="text-lg font-semibold text-zinc-900">{t.preciosCalculados}</h2>

          {/* Summary KPIs */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <p className="text-xs text-zinc-500 mb-1">{t.foodCostMedio}</p>
              <p className="text-2xl font-bold text-zinc-900">
                {pct1(totals.avgFoodCost)}%
              </p>
            </div>
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <p className="text-xs text-zinc-500 mb-1">{t.margenMedio}</p>
              <p className="text-2xl font-bold text-emerald-600">
                {pct1(totals.avgMargen)}%
              </p>
            </div>
          </div>

          {/* Results per dish */}
          <div className="space-y-3">
            {results.map((r) => (
              <div key={r.id} className="bg-white rounded-xl border border-zinc-200 p-4">
                <div className="flex justify-between items-start mb-3">
                  <span className="font-medium text-zinc-900">{r.nombre || t.sinNombre}</span>
                  <div className="text-right">
                    <span className="text-lg font-bold text-lime-600">{eur(r.pvpConIva)}</span>
                    <span className="text-xs text-zinc-400 block">{t.ivaInc}</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                  <div>
                    <span className="text-zinc-500 block">{t.sinIva}</span>
                    <span className="font-medium">{eur(r.pvpSinIva)}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500 block">{t.iva(ivaPct)}</span>
                    <span className="font-medium">{eur(r.iva)}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500 block">{t.margen}</span>
                    <span className="font-medium text-emerald-600">{eur(r.margen)}</span>
                  </div>
                </div>
                {r.tip && (
                  <div className="flex items-start gap-2 bg-amber-50 rounded-lg p-2">
                    <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-700">
                      <span className="font-medium">{t.precioSugerido}: {eur(r.pvpSugerido)}</span>
                      <span className="block text-amber-600">{r.tip}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Psychological pricing tips */}
          <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-violet-800 mb-2">🧠 Psychological Pricing</h3>
            <ul className="text-sm text-violet-700 space-y-1">
              {t.consejos.map((c) => (
                <li key={c}>• {c}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
